<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="75%">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="updateAlertConfig">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Edit "{{ config.name }}" Device Config</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['name']" autofocus :rules="[vRules.required]" density="compact"
                                          label="Name"/>
                        </v-col>
                        <v-col v-if="user.type === 'admin'" class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['company_id']"
                                      :items="companies"
                                      :rules="[vRules.required]"
                                      density="compact" label="Company"
                                      @update:modelValue="()=>{ resetForm(); fetchAlertTemplates('general');
                                          fetchTimeSchedules(); fetchNotificationGroups();
                                      }">
                            </company-select>
                        </v-col>
                        <v-col class="py-0 append-btn" cols="12" sm="6">
                            <v-select v-model="formData['monitoring_notification_group_id']"
                                      :items="notificationGroups"
                                      :rules="[vRules.required]"
                                      density="compact" item-title="label" item-value="id"
                                      label="Notification Recipients">
                                <template v-slot:append>
                                    <v-icon class="cursor-pointer" icon="mdi-plus" size="small"
                                            :disabled="!formData['company_id']"
                                            @click="recipientsDialog = true"/>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col v-if="user.type === 'admin'" class="pa-0" cols="12" sm="6">
                            <v-checkbox v-model="formData['override_general_config']" label="Override General Config"/>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="py-0 my-3" cols="12">
                            <h4>Configs</h4>

                            <v-table v-if="formData['company_id']">
                                <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Profile</th>
                                    <th>Schedule</th>
                                    <th style="width: 15px">
                                        <v-btn icon="mdi-plus" size="small" variant="flat" @click="addTemplate"/>
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <template v-for="(alert,index) in formData['templates']">
                                    <tr v-if="alert['alert_type']!=='general' || user.type === 'admin'" :key="index">
                                        <td class="pa-0">
                                            <v-select v-model="alert['alert_type']"
                                                      :items="alertTypes"
                                                      :readonly="alert['alert_type']==='general'"
                                                      :rules="[vRules.required]"
                                                      class="text-capitalize" density="compact" label="Type"
                                                      @update:model-value="() => { alert['alert_template'] = null; alert['time_schedule'] = null; fetchAlertTemplates(alert['alert_type']); }">
                                            </v-select>
                                        </td>
                                        <td class="pa-0 append-btn">
                                            <v-select v-model="alert['alert_template']"
                                                      :items="alertTemplates[alert['alert_type']]"
                                                      :rules="[vRules.required]" density="compact" item-title="label"
                                                      item-value="id"
                                                      label="Profile">
                                                <template v-slot:append>
                                                    <v-icon v-if="alert['alert_template'] && !(alert.alert_type === 'general' && !user?.super)"
                                                            class="cursor-pointer me-1"
                                                            icon="mdi-pencil"
                                                            size="small"
                                                            title="Edit selected profile"
                                                            @click="openEditTemplate(alert, index)"/>
                                                    <v-icon v-if="!(alert.alert_type === 'general' && !user?.super)"
                                                            :disabled="!alert['alert_type']"
                                                            class="cursor-pointer"
                                                            icon="mdi-plus"
                                                            size="small"
                                                            title="Add new profile"
                                                            @click="()=>{
                                                                selectedAlertType=alert['alert_type'];
                                                                selectedRowIndex=index;
                                                                templateDialog = true;
                                                            }"/>
                                                </template>
                                            </v-select>
                                        </td>
                                        <td class="pa-0 append-btn">
                                            <v-select v-model="alert['time_schedule']"
                                                      :items="timeSchedules"
                                                      :rules="[vRules.required]" density="compact" item-title="label"
                                                      item-value="id" label="Schedule">
                                                <template v-slot:append>
                                                    <v-icon class="cursor-pointer" icon="mdi-plus" size="small"
                                                            @click="()=>{
                                                                selectedRowIndex=index;
                                                                scheduleDialog = true;
                                                            }"/>
                                                </template>
                                            </v-select>
                                        </td>
                                        <td class="pa-0 text-center">
                                            <v-btn
                                                v-if="alert['alert_type']!=='general' || !formData['override_general_config']"
                                                class="mb-3" icon="mdi-close" size="small" variant="text"
                                                @click="removeTemplate(index)"/>
                                        </td>
                                    </tr>
                                </template>
                                </tbody>
                            </v-table>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>

    <Create-Schedule v-if="scheduleDialog" v-model="scheduleDialog" :company-id="formData['company_id']"
                     @addSchedule="(newSchedule)=>{
                         setTemplateValue('time_schedule',newSchedule.id);
                         fetchTimeSchedules(false);
                     }"/>

    <Create-Template v-if="templateDialog" v-model="templateDialog" :company-id="formData['company_id']"
                     :type="selectedAlertTemplate"
                     @addTemplate="(newTemplate)=>{
                         setTemplateValue('alert_template',newTemplate.id);
                         fetchAlertTemplates(selectedAlertType,true)
                     }"/>

    <Edit-Template v-if="editTemplateDialog && editingTemplate" v-model="editTemplateDialog"
                   :type="selectedAlertTemplate" :template="editingTemplate"
                   @updateTemplate="()=>{ fetchAlertTemplates(selectedAlertType, true); }"/>

    <Recipients v-if="recipientsDialog" v-model="recipientsDialog" :company="formData['company_id']"
                @addGroup="(newNotificationGroup)=>{
                    setTemplateValue('notification_group',newNotificationGroup.id);
                    fetchNotificationGroups(false);
                }"/>
</template>

<script setup>
import {ref, reactive, onMounted, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import CreateSchedule from "@/components/dialogs/monitoring/schedules/Create.vue";
import CreateTemplate from "@/components/dialogs/monitoring/templates/Create.vue";
import EditTemplate from "@/components/dialogs/monitoring/templates/Edit.vue";
import Recipients from "@/components/dialogs/monitoring/notifications/Recipients.vue";
import CompanySelect from "@/components/globals/company-select.vue";

const props = defineProps({
    modelValue: Boolean,
    config: Object,
});
const formReady = ref(false);
const configDetails = ref();
const errors = ref([]);
const {user} = useAuthStore();
const companies = ref([]);
const timeSchedules = ref([]);
const alertTemplates = ref({
    'general': [],
    'speed': [],
    'geofence': [],
    'light': [],
    'temperature': [],
    'ble': [],
    'wifi': [],
    'push_button': [],
    'io': [],
});
const notificationGroups = ref([]);
const scheduleDialog = ref(false);
const templateDialog = ref(false);
const recipientsDialog = ref(false);
const selectedRowIndex = ref(null);
const selectedAlertType = ref(null);
const selectedAlertTemplate = computed(() => {
    switch (selectedAlertType.value) {
        case 'ble':
        case 'wifi':
            return selectedAlertType.value + '_tag';
        default:
            return selectedAlertType.value?.toLowerCase().replace(' ', '_') ?? null;
    }
});
const editTemplateDialog = ref(false);
const editingTemplate = ref(null);

// API resource prefix for fetching/saving a full template of the given alert type.
const templatePrefix = (alertType) => {
    switch (alertType) {
        case 'geofence':
            return 'monitoring/geofence';
        case 'light':
            return 'monitoring/light-sensor';
        case 'temperature':
            return 'monitoring/temperature';
        case 'speed':
            return 'monitoring/speed';
        case 'push_button':
            return 'monitoring/push-button';
        case 'io':
            return 'monitoring/ext-gpio';
        case 'ble':
        case 'wifi':
            return 'monitoring/ble-wifi';
        default:
            return 'monitoring/general';
    }
};

// Edit the currently selected profile. The dropdown only carries {id,label}, so
// fetch the full template before opening the editor.
const openEditTemplate = async (alert, index) => {
    if (!alert['alert_template']) return;
    selectedAlertType.value = alert['alert_type'];
    selectedRowIndex.value = index;
    try {
        const response = await send(
            'get',
            `${templatePrefix(alert['alert_type'])}-templates/${alert['alert_template']}`,
            user.type === 'admin' ? {company: formData['company_id']} : {}
        );
        editingTemplate.value = response.data.data;
        editTemplateDialog.value = true;
    } catch (error) {
        showSnackbar('Could not load the profile to edit', 'error');
    }
};
const {showSnackbar} = useSnackbarStore();
const dialogSwitch = ref(props.modelValue);
const getConfigType = (alertAbleType) => {
    switch (alertAbleType) {
        case 'App\\Models\\MonitoringGeneralTemplate':
            return 'general'
        case 'App\\Models\\MonitoringSpeedTemplate':
            return 'speed'
        case 'App\\Models\\MonitoringGeofenceTemplate':
            return 'geofence'
        case 'App\\Models\\MonitoringLightSensorTemplate':
            return 'light'
        case 'App\\Models\\MonitoringTemperatureSensorTemplate':
            return 'temperature'
        case 'App\\Models\\MonitoringPushButtonTemplate':
            return 'push_button'
        case 'App\\Models\\MonitoringExtGpioTemplate':
            return 'io'
        case 'App\\Models\\MonitoringBleWifiTemplate':
            return configDetails.value.type ?? ''
    }
}
const formData = reactive({
    valid: false,
    name: props.config.name,
    company_id: props.config.company_id,
    override_general_config: !!props.config.override_general_config,
    monitoring_notification_group_id: props.config.monitoring_notification_group_id,
    templates: [
        {
            alert_type: 'general',
            alert_template: null,
            time_schedule: null,
        }
    ],
});
const emits = defineEmits(['update:modelValue', 'updateConfig']);

onMounted(async () => {
    await fetchConfigDetails();
    const promises = [fetchTimeSchedules(false), fetchNotificationGroups(false)];
    if (user.type === 'admin') {
        promises.push(fetchCompanies(false));
    }
    if (configDetails.value && configDetails.value.templates.length > 0) {
        formData['templates'] = [];
        configDetails.value.templates.map((template) => {
            formData['templates'].push({
                alert_template: template.monitoring_alertable_id,
                time_schedule: template.monitoring_time_schedule_id,
                alert_type: template.type ?? getConfigType(template.monitoring_alertable_type),
            })
            promises.push(fetchAlertTemplates(template.type ?? getConfigType(template.monitoring_alertable_type)));
        })
    }
    Promise.all(promises).finally(() => formReady.value = true);
});

const alertTypes = computed(() => {
    const options = ['speed', 'geofence', 'light', 'temperature', 'ble', 'wifi', 'push_button', 'io'];
    if (user.type === 'admin') {
        const GC = formData['templates'].find((template) => template['alert_type'] === 'general');
        if (!GC) {
            options.unshift('general')
        }
    }
    return options;
});

const setTemplateValue = (prop = 'alert_template', val) => {
    switch (prop) {
        case 'alert_template':
            formData['templates'][selectedRowIndex.value]['alert_template'] = val;
            break;
        case 'time_schedule':
            formData['templates'][selectedRowIndex.value]['time_schedule'] = val;
            break;
        case 'notification_group':
            formData['monitoring_notification_group_id'] = val;
            break;
    }
}
const resetForm = () => {
    formData['templates'] = [
        {
            alert_type: 'general',
            alert_template: null,
            time_schedule: null,
        }
    ];
    formData['monitoring_notification_group_id'] = null;
    alertTemplates.value = {
        'general': [],
        'speed': [],
        'geofence': [],
        'light': [],
        'temperature': [],
        'push_button': [],
        'ble': [],
        'wifi': [],
        'io': [],
    };
}
const addTemplate = () => {
    formData['templates'].push({
        alert_type: null,
        alert_template: null,
        time_schedule: null,
    })
}
const removeTemplate = (index) => {
    formData['templates'].splice(index, 1)
}
const fetchConfigDetails = () => {
    return send('get', `monitoring/configs/${props.config.id}`)
        .then((response) => {
            configDetails.value = response.data.data;
        })
        .catch(() => {
            showSnackbar('Monitoring config details could not be loaded', 'error')
        });
}
const fetchCompanies = (reset = true) => {
    reset ? formData['company_id'] = null : '';
    return send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        });
}
const fetchTimeSchedules = (reset = true) => {
    reset ? formData['monitoring_time_schedule_id'] = null : '';
    return send('get', `monitoring/time-schedules?`, {list: true, company: formData['company_id']})
        .then((response) => {
            timeSchedules.value = response.data.data;
        })
        .catch(() => {
            showSnackbar('Time Schedules could not be loaded', 'error')
        })
}
const fetchAlertTemplates = (alert_type, force = false) => {
    if (!alertTemplates.value[alert_type].length || force) {
        return send('get', `templates/list?${(user.type === 'admin') ? "company=" + formData['company_id'] : ''}`, {type: alert_type})
            .then((response) => {
                alertTemplates.value[alert_type] = response.data.data;
            })
            .catch(() => {
                showSnackbar('Alert Templates could not be loaded', 'error')
            })
    }
    return Promise.resolve();
}
const fetchNotificationGroups = (reset = true) => {
    reset ? formData['monitoring_notification_group_id'] = null : '';
    return send('get', `monitoring/notification-groups?`, {list: true, company: formData['company_id']})
        .then((response) => {
            notificationGroups.value = response.data.data;
        })
        .catch(() => {
            showSnackbar('Monitoring notification groups could not be loaded', 'error')
        })
}
const updateAlertConfig = () => {
    errors.value = [];
    send('put', `monitoring/configs/${props.config.id}`, formData)
        .then((response) => {
            showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updateConfig', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>

<style>
.append-btn .v-input__append {
    margin: 0;
}
</style>
